'use client';

import React, { useState } from 'react';
import { 
  Calendar as CalendarIcon, 
  Target, 
  CheckCircle2, 
  UserX, 
  Eye, 
  EyeOff, 
  DollarSign,
  CreditCard,
  Smartphone,
  Repeat,
  Lock,
  X
} from 'lucide-react';
import { Appointment, ChannelSummary } from './types';

interface BookingsKpiStripProps {
  appointments: Appointment[];
  channelSummary: ChannelSummary;
  totalCollected: number;
}

export const BookingsKpiStrip: React.FC<BookingsKpiStripProps> = ({
  appointments,
  channelSummary,
  totalCollected,
}) => {
  const [isMasked, setIsMasked] = useState(true);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleToggleMask = () => {
    if (!isMasked) {
      setIsMasked(true);
      return;
    }
    // Para ver números reales, pedir PIN de mostrador
    setShowPinModal(true);
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setPinError('');
    try {
      const res = await fetch('/api/auth/me', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput })
      });
      const data = await res.json();
      if (data.valid) {
        setIsMasked(false);
        setShowPinModal(false);
        setPinInput('');
      } else {
        setPinError('PIN incorrecto. Ingresá el PIN de mostrador configurado.');
      }
    } catch (err) {
      setPinError('Error de verificación');
    } finally {
      setVerifying(false);
    }
  };

  // Métricas reales calculadas desde los appointments del día
  const totalCount = appointments.length;
  const confirmedCount = appointments.filter(
    (a) => a.status.toLowerCase() === 'confirmed' || a.status.toLowerCase() === 'completed'
  ).length;
  const noShowCount = appointments.filter(
    (a) => a.status.toLowerCase() === 'no_show' || a.status.toLowerCase() === 'cancelled'
  ).length;
  const attendedCount = appointments.filter(
    (a) => a.status.toLowerCase() === 'completed'
  ).length;

  const attendancePercent =
    totalCount > 0 ? Math.round((confirmedCount / totalCount) * 100) : 0;

  const maskValue = (val: number) => {
    return isMasked
      ? '$ •••••'
      : `$ ${val.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`;
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-3 px-4 sm:px-6 py-4">
      {/* 1. TURNOS */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs relative overflow-hidden flex flex-col justify-between">
        <div>
          <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
            TURNOS
          </span>
          <span className="block text-2xl font-black text-[#0f172a] mt-1">
            {totalCount}
          </span>
        </div>
        <span className="text-[11px] font-semibold text-slate-400 mt-2">
          {totalCount === 0
            ? 'sin turnos'
            : `${totalCount} ${totalCount === 1 ? 'activo' : 'activos'}`}
        </span>
        <CalendarIcon className="w-10 h-10 text-slate-100 absolute -bottom-1 -right-1 pointer-events-none" />
      </div>

      {/* 2. ASISTENCIA */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs relative overflow-hidden flex flex-col justify-between">
        <div>
          <span className="block text-[10px] font-black text-emerald-600 uppercase tracking-widest">
            ASISTENCIA
          </span>
          <span className="block text-2xl font-black text-[#0f172a] mt-1">
            {totalCount === 0 ? '—' : `${attendancePercent}%`}
          </span>
        </div>
        <span className="text-[11px] font-semibold text-slate-400 mt-2 truncate">
          {totalCount === 0
            ? 'sin turnos para medir...'
            : `${attendedCount} de ${totalCount}`}
        </span>
        <Target className="w-10 h-10 text-slate-100 absolute -bottom-1 -right-1 pointer-events-none" />
      </div>

      {/* 3. CONFIRMADOS */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs relative overflow-hidden flex flex-col justify-between">
        <div>
          <span className="block text-[10px] font-black text-sky-600 uppercase tracking-widest">
            CONFIRMADOS
          </span>
          <span className="block text-2xl font-black text-[#0f172a] mt-1">
            {totalCount === 0 ? '—' : confirmedCount}
          </span>
        </div>
        <span className="text-[11px] font-semibold text-slate-400 mt-2 truncate">
          {totalCount === 0 ? 'sin turnos' : `${confirmedCount} del día`}
        </span>
        <CheckCircle2 className="w-10 h-10 text-slate-100 absolute -bottom-1 -right-1 pointer-events-none" />
      </div>

      {/* 4. NO VINIERON */}
      <div className="lg:col-span-2 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-2xs relative overflow-hidden flex flex-col justify-between">
        <div>
          <span className="block text-[10px] font-black text-rose-600 uppercase tracking-widest">
            NO VINIERON
          </span>
          <span className="block text-2xl font-black text-[#0f172a] mt-1">
            {noShowCount}
          </span>
        </div>
        <span className="text-[11px] font-semibold text-slate-400 mt-2 truncate">
          {noShowCount === 0 ? 'no se cayó ninguno' : `${noShowCount} caídos`}
        </span>
        <UserX className="w-10 h-10 text-slate-100 absolute -bottom-1 -right-1 pointer-events-none" />
      </div>

      {/* 5. TARJETA FINANCIERA (7 Canales + Enmascaramiento con Ojo) */}
      <div className="col-span-2 md:col-span-4 lg:col-span-4 bg-[#ecfdf5]/80 border border-[#a7f3d0] rounded-2xl p-3 sm:p-4 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Desglose de los 7 canales */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-bold text-slate-600 w-full sm:w-auto">
          <span>💵 E. {maskValue(channelSummary.EFECTIVO)}</span>
          <span>💳 C. {maskValue(channelSummary.CREDITO)}</span>
          <span>💳 D. {maskValue(channelSummary.DEBITO)}</span>
          <span>⇆ T. {maskValue(channelSummary.TRANSFERENCIA)}</span>
          <span>📱 M. {maskValue(channelSummary.MERCADOPAGO)}</span>
          <span>🔗 O. {maskValue(channelSummary.OTRO)}</span>
          <span>💰 S. {maskValue(channelSummary.SENAS)}</span>
        </div>

        {/* Totalizador COBRADO */}
        <div className="flex flex-col items-end shrink-0 border-t sm:border-t-0 sm:border-l border-emerald-200/80 pt-2 sm:pt-0 sm:pl-3 w-full sm:w-auto">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-black text-emerald-800 uppercase tracking-wider">
              COBRADO
            </span>
            <button
              onClick={handleToggleMask}
              title={isMasked ? 'Desbloquear métricas con PIN de mostrador' : 'Ocultar valores'}
              className="text-emerald-700 hover:text-emerald-900 cursor-pointer p-0.5"
            >
              {isMasked ? (
                <EyeOff className="w-3.5 h-3.5" />
              ) : (
                <Eye className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
          <span className="text-lg sm:text-xl font-black text-emerald-900 tracking-tight mt-0.5">
            {maskValue(totalCollected)}
          </span>
        </div>
      </div>

      {/* Modal PIN de Desbloqueo Financiero */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 flex flex-col gap-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Lock className="w-4 h-4" />
                </div>
                <h3 className="font-bold text-sm text-slate-900">Desbloquear Métricas</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPinModal(false);
                  setPinInput('');
                  setPinError('');
                }}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Ingresá el PIN de mostrador configurado en <span className="font-semibold">Negocio &gt; Equipo</span> para ver los números de facturación reales.
            </p>

            <form onSubmit={handleVerifyPin} className="space-y-3">
              <input
                type="password"
                maxLength={8}
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Ingresá tu PIN (ej: 1234)"
                autoFocus
                className="w-full text-center tracking-widest text-lg font-black px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-emerald-600 focus:outline-none"
              />

              {pinError && (
                <span className="text-[11px] font-semibold text-rose-600 block text-center">
                  {pinError}
                </span>
              )}

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPinModal(false)}
                  className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={verifying || !pinInput}
                  className="flex-1 py-2 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-sm transition disabled:opacity-50"
                >
                  {verifying ? 'Verificando...' : 'Desbloquear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
